import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import {
  ActionButton,
  AppTable,
  ColumnHeader,
  TableSpinner,
  TextColumn,
} from "../../components";
import DeleteModal from "../../components/Common/DeleteModal";
import {
  ActionTypeEnum,
  CourseEnum,
  LiveLectureAccessModeEnum,
  LiveLectureStatusEnum,
  RoleEnum,
} from "../../enums";
import { LiveLectureDto, PaginateQuery } from "../../interfaces";
import { Paginated } from "../../models";
import { AccountState } from "../../store/slices/accountSlice";
import {
  useDeleteLiveLectureMutation,
  useGetLiveLecturesQuery,
  useLazyGetSessionMeetingLinkQuery,
  usePublishLiveLectureMutation,
} from "../../store/api/liveLectureApi";
import { useGetCourseSelectListQuery } from "../../store/api/courseApi";
import { AddEditLiveLectureModal } from "./AddEditLiveLectureModal";
import {
  formatLiveLectureDate,
  formatLiveLectureTimeSlot,
  canEditLiveLecture,
  canJoinLiveLecture,
  canManageLiveLecture,
  isPastLiveLecture,
} from "./liveLectureCalendarUtils";
import { openLiveCall } from "./openLiveCall";
import { LiveLectureRecordingsModal } from "./LiveLectureRecordings";
import { isRecordingPending, lectureRecordings } from "./recordingMedia";
import {
  lectureRoomId,
  LiveLecturePresenceLine,
  useLiveLectureClock,
  useLiveLecturePresence,
} from "./useLiveLecturePresence";

const joinableStatuses = [
  LiveLectureStatusEnum.PUBLISHED,
  LiveLectureStatusEnum.FULL,
  LiveLectureStatusEnum.IN_PROGRESS,
];

export const SubjectLiveLecturePage: React.FC = () => {
  const {
    subjectId,
    gradeId,
    course: courseParam,
  } = useParams<{
    subjectId: string;
    gradeId?: string;
    course?: string;
  }>();
  const { t, i18n } = useTranslation();
  const { data: courseSelectList } = useGetCourseSelectListQuery();
  const courseId = useMemo(() => {
    if (!courseSelectList?.length || !courseParam) {
      return undefined;
    }

    const normalized = courseParam.toLowerCase();
    if (normalized === CourseEnum.FIRST_COURSE) {
      return courseSelectList.find(
        (course) => course.value.en.toLowerCase() === "first course",
      )?.id;
    }

    if (normalized === CourseEnum.SECOND_COURSE) {
      return courseSelectList.find(
        (course) => course.value.en.toLowerCase() === "second course",
      )?.id;
    }

    return undefined;
  }, [courseSelectList, courseParam]);
  const history = useHistory();
  const profile = useSelector(
    (state: { account: AccountState }) => state.account.userProfile,
  );
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const initState = new PaginateQuery(10, "start_at:ASC", {
    subjectId: subjectId as string,
    accessMode: LiveLectureAccessModeEnum.SUBJECT_INCLUDED,
  });

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(initState);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [recordingLecture, setRecordingLecture] =
    useState<LiveLectureDto | null>(null);
  const [recordingPollInterval, setRecordingPollInterval] = useState(0);

  const {
    data: response,
    isLoading,
    refetch,
  } = useGetLiveLecturesQuery(paginateQuery, {
    pollingInterval: recordingPollInterval,
  });
  const [getSessionMeetingLink] = useLazyGetSessionMeetingLinkQuery();
  const [deleteLiveLecture, { isLoading: isLoadingDelete }] =
    useDeleteLiveLectureMutation();
  const [publishLiveLecture] = usePublishLiveLectureMutation();

  const isSuperAdmin = profile?.role?.key === RoleEnum.SUPER_ADMIN;
  const isTeacherAdmin = profile?.role?.key === RoleEnum.TEACHER_ADMIN;
  const isHeadTeacherAdmin = profile?.role?.key === RoleEnum.HEAD_TEACHER_ADMIN;
  const isSystemAdmin = profile?.role?.key === RoleEnum.SYSTEM_ADMIN;
  const isAdmin = profile?.role?.key === RoleEnum.ADMIN;
  const canCreateLiveLecture =
    isSuperAdmin ||
    isSystemAdmin ||
    isTeacherAdmin ||
    isHeadTeacherAdmin ||
    isAdmin;

  const canManageRow = (row: LiveLectureDto) =>
    canManageLiveLecture(row, profile);

  const tableData = useMemo(() => {
    return (response as Paginated<LiveLectureDto>)?.data || [];
  }, [response]);
  const now = useLiveLectureClock();
  const presenceByRoom = useLiveLecturePresence(tableData, now);

  useEffect(() => {
    const pending = tableData.some((lecture) =>
      lectureRecordings(lecture.media).some(isRecordingPending),
    );
    setRecordingPollInterval(pending ? 15000 : 0);
  }, [tableData]);

  const onJoin = useCallback(
    async (row: LiveLectureDto) => {
      setJoiningId(row.id);
      try {
        const sessionLink = await getSessionMeetingLink(row.id).unwrap();
        if (sessionLink.canJoin && sessionLink.token) {
          const joined = await openLiveCall(sessionLink, history, {
            title: row.title?.[langKey],
            returnPath: history.location.pathname,
          });
          if (!joined) {
            toast.error(t("liveCallMicPermissionDenied"));
          }
        } else {
          toast.error(t("joinLiveLectureNotAvailable"));
        }
      } catch {
        toast.error(t("joinLiveLectureError"));
      } finally {
        setJoiningId(null);
      }
    },
    [getSessionMeetingLink, history, langKey, t],
  );

  const onAdd = () => {
    setEditingItemId(null);
    setShowAddEditModal(true);
  };

  const onEdit = (id: string) => {
    setEditingItemId(id);
    setShowAddEditModal(true);
  };

  const onPublish = async (id: string) => {
    await publishLiveLecture(id);
  };

  const onDelete = (id: string) => {
    setDeletingItemId(id);
    setShowDeleteModal(true);
  };

  const columns: TableColumn<LiveLectureDto>[] = useMemo(() => {
    const baseColumns: TableColumn<LiveLectureDto>[] = [
      {
        name: <ColumnHeader title="title" />,
        cell: (row) => (
          <TextColumn
            text={row.title[i18n.language === "ar" ? "ar" : "en"]}
            isNeedToTranslate={false}
          />
        ),
        sortable: true,
      },
    ];

    if (!isTeacherAdmin) {
      baseColumns.push({
        name: <ColumnHeader title="teacher" />,
        cell: (row) => (
          <TextColumn
            text={
              row.teacher?.name?.[i18n.language === "ar" ? "ar" : "en"] || "-"
            }
            isNeedToTranslate={false}
          />
        ),
        sortable: false,
      });
    }

    baseColumns.push(
      {
        name: <ColumnHeader title="time" />,
        cell: (row) => (
          <div>
            <div>{formatLiveLectureDate(row.startAt)}</div>
            <div className="text-muted small">
              {formatLiveLectureTimeSlot(row.startAt, row.endAt)}
            </div>
          </div>
        ),
        sortable: true,
      },
      {
        name: <ColumnHeader title="duration" />,
        cell: (row) => (
          <TextColumn
            text={`${row.durationMinutes} ${t("minutes")}`}
            isNeedToTranslate={false}
          />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="status" />,
        cell: (row) => (
          <TextColumn text={t(`liveLectureStatus_${row.status}`)} />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="liveLectureCall" />,
        minWidth: "180px",
        cell: (row) => (
          <LiveLecturePresenceLine
            startAt={row.startAt}
            endAt={row.endAt}
            now={now}
            presence={presenceByRoom[lectureRoomId(row)]}
          />
        ),
        sortable: false,
      },
      {
        name: <ColumnHeader title="action" />,
        sortable: false,
        minWidth: "420px",
        grow: 0,
        cell: (row) => {
          const recordings = lectureRecordings(row.media);
          const recordingPending = recordings.some(isRecordingPending);
          const recordingLabel = recordingPending
            ? t("liveLectureRecordingInProgress", {
                defaultValue: "In progress",
              })
            : t("liveLectureRecordings", { defaultValue: "Recordings" });

          return (
            <div className="d-flex align-items-center gap-1">
              {canJoinLiveLecture(profile) &&
              joinableStatuses.includes(row.status) &&
              !isPastLiveLecture(row.endAt, now) ? (
                <Button
                  color="primary"
                  size="sm"
                  title={t("joinLiveLecture")}
                  disabled={joiningId === row.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    onJoin(row);
                  }}
                >
                  {joiningId === row.id ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    <i className="ri-live-line me-1"></i>
                  )}
                  {t("joinLiveLecture")}
                </Button>
              ) : null}
              {recordings.length > 0 ? (
                <Button
                  color="primary"
                  size="sm"
                  title={recordingLabel}
                  onClick={(event) => {
                    event.stopPropagation();
                    setRecordingLecture(row);
                  }}
                >
                  <i
                    className={
                      recordingPending
                        ? "ri-time-line me-1"
                        : "ri-play-circle-line me-1"
                    }
                  ></i>
                  {recordingLabel}
                </Button>
              ) : null}
              {canManageRow(row) && canEditLiveLecture(row) && (
                <ActionButton
                  onAction={() => onEdit(row.id)}
                  actionType={ActionTypeEnum.EDIT}
                />
              )}
              {canManageRow(row) && (
                <ActionButton
                  onAction={() => onDelete(row.id)}
                  actionType={ActionTypeEnum.DELETE}
                />
              )}
            </div>
          );
        },
      },
    );

    return baseColumns;
  }, [
    isTeacherAdmin,
    i18n.language,
    joiningId,
    now,
    onJoin,
    presenceByRoom,
    profile,
    t,
    isSuperAdmin,
  ]);

  const onDeleteConfirm = async () => {
    if (!deletingItemId) return;
    await deleteLiveLecture(deletingItemId).unwrap();
    setDeletingItemId(null);
    setShowDeleteModal(false);
  };

  const getSortByColumnName = (name: string) => {
    let sortBy;
    switch (name) {
      case "title.en":
      case "title.ar":
        sortBy = 1;
        break;
      case "start_at":
        sortBy = 3;
        break;
      default:
        sortBy = 3;
        break;
    }
    return sortBy;
  };

  const onSearch = (searchText: string) => {
    setPaginateQuery({ ...paginateQuery, search: searchText, page: 1 });
  };

  const getSortByColumnId = (columnId: number, sortDirection: string) => {
    let sortBy;
    switch (columnId) {
      case 1:
        sortBy = "title" + "." + i18n.language;
        break;
      case 3:
        sortBy = "start_at";
        break;
      default:
        sortBy = "start_at";
        break;
    }
    return `${sortBy}:${sortDirection.toUpperCase()}`;
  };

  const [defaultSortFieldId, setDefaultSortFieldId] = useState(
    getSortByColumnName("sequence_number:ASC"),
  );
  const [defaultSortAsc, setDefaultSortAsc] = useState(false);
  const [defaultPage, setDefaultPage] = useState(1);

  const onChangePage = (page: number) => {
    setPaginateQuery({ ...paginateQuery, page: page });
  };

  const onChangeRowsPerPage = (
    currentRowsPerPage: number,
    currentPage: number,
  ) => {
    setPaginateQuery({
      ...paginateQuery,
      limit: currentRowsPerPage,
      page: currentPage,
    });
  };

  const onSort = (
    column: TableColumn<LiveLectureDto>,
    sortDirection: string,
  ) => {
    const sortBy = getSortByColumnId(column.id as number, sortDirection);
    setPaginateQuery({
      ...paginateQuery,
      sortBy: sortBy as string,
    });
  };

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("liveLectures")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button
                color="success"
                onClick={() => {
                  setPaginateQuery({ ...paginateQuery });
                  refetch();
                }}
              >
                {t("refresh")}
              </Button>
            </div>
          </Col>
        </Row>
        <Row>
          <Col xxl={12}>
            <Card>
              <CardBody>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="search-box">
                      <DebounceInput
                        className="form-control"
                        placeholder={`${t("searchPlaceholder")}...`}
                        minLength={2}
                        debounceTimeout={300}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>,
                        ) => onSearch(event.target.value)}
                        value={paginateQuery.search}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>
                  <Col md={6} className="text-end">
                    {canCreateLiveLecture && (
                      <Button color="success" onClick={onAdd}>
                        <i className="ri-add-line align-bottom me-1"></i>
                        {t("addItem", { item: "liveLecture" })}
                      </Button>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col sm={12}>
                    <AppTable
                      columns={columns}
                      data={tableData}
                      progressPending={isLoading}
                      progressComponent={<TableSpinner />}
                      pagination
                      paginationServer
                      sortServer
                      onSort={onSort}
                      onChangeRowsPerPage={onChangeRowsPerPage}
                      onChangePage={onChangePage}
                      defaultSortFieldId={defaultSortFieldId}
                      paginationDefaultPage={defaultPage}
                      defaultSortAsc={defaultSortAsc}
                      paginationTotalRows={response?.meta?.totalItems}
                      paginationComponentOptions={{
                        rowsPerPageText: t("rowsPerPage"),
                        rangeSeparatorText: t("of"),
                      }}
                    />
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <AddEditLiveLectureModal
        key={editingItemId || "create"}
        id={editingItemId}
        show={showAddEditModal}
        onCloseClick={() => {
          setShowAddEditModal(false);
          setEditingItemId(null);
        }}
        accessMode={LiveLectureAccessModeEnum.SUBJECT_INCLUDED}
        subjectId={subjectId}
        gradeId={gradeId}
        courseId={courseId}
      />

      <DeleteModal
        loading={isLoadingDelete}
        show={showDeleteModal}
        onDeleteClick={onDeleteConfirm}
        onCloseClick={() => setShowDeleteModal(false)}
      />
      <LiveLectureRecordingsModal
        lecture={
          recordingLecture
            ? tableData.find((item) => item.id === recordingLecture.id) ||
              recordingLecture
            : null
        }
        show={Boolean(recordingLecture)}
        langKey={langKey}
        onClose={() => setRecordingLecture(null)}
      />
    </React.Fragment>
  );
};
